import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { buildApiUrl } from '../config/api';
import { logger } from '../utils/logger';

type Kind = 'servicio' | 'factura' | 'cliente';

interface Counts {
  servicios: number;
  facturas: number;
  clientes: number;
}

interface AccountSummaryValue {
  loading: boolean;
  counts: Counts;
  showDemoData: boolean;
  hasServicios: boolean;
  hasFacturas: boolean;
  hasClientes: boolean;
  /** Cuenta sin ningún dato real. Siempre false mientras carga, para no parpadear */
  isEmptyAccount: boolean;
  refresh: () => Promise<void>;
  /** Incremento optimista: la invitación desaparece al instante, sin refetch */
  noteCreated: (kind: Kind) => void;
}

const emptyCounts: Counts = { servicios: 0, facturas: 0, clientes: 0 };

const AccountSummaryContext = createContext<AccountSummaryValue>({
  loading: true,
  counts: emptyCounts,
  showDemoData: false,
  hasServicios: true,
  hasFacturas: true,
  hasClientes: true,
  isEmptyAccount: false,
  refresh: async () => {},
  noteCreated: () => {},
});

export const AccountSummaryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState<Counts>(emptyCounts);
  const [showDemoData, setShowDemoData] = useState(false);

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(buildApiUrl('/api/user/account-summary'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setCounts({
        servicios: Number(data.servicios) || 0,
        facturas: Number(data.facturas) || 0,
        clientes: Number(data.clientes) || 0,
      });
      setShowDemoData(Boolean(data.showDemoData));
    } catch (error) {
      logger.error('AccountSummary: fetch failed', error);
      // Ante un fallo no invitamos a nada: mejor mostrar la app normal que un vacío falso
      setCounts({ servicios: 1, facturas: 1, clientes: 1 });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      setLoading(true);
      setCounts(emptyCounts);
      return;
    }
    refresh();
  }, [token, refresh]);

  const noteCreated = useCallback((kind: Kind) => {
    setCounts(prev => ({
      servicios: prev.servicios + (kind === 'servicio' ? 1 : 0),
      facturas: prev.facturas + (kind === 'factura' ? 1 : 0),
      clientes: prev.clientes + (kind === 'cliente' ? 1 : 0),
    }));
  }, []);

  const value = useMemo<AccountSummaryValue>(() => {
    const hasServicios = showDemoData || counts.servicios > 0;
    const hasFacturas = showDemoData || counts.facturas > 0;
    const hasClientes = showDemoData || counts.clientes > 0;
    return {
      loading,
      counts,
      showDemoData,
      hasServicios,
      hasFacturas,
      hasClientes,
      isEmptyAccount: !loading && !hasServicios && !hasFacturas && !hasClientes,
      refresh,
      noteCreated,
    };
  }, [loading, counts, showDemoData, refresh, noteCreated]);

  return (
    <AccountSummaryContext.Provider value={value}>
      {children}
    </AccountSummaryContext.Provider>
  );
};

export const useAccountSummary = () => useContext(AccountSummaryContext);
